import {
  Alert,
  Anchor,
  Badge,
  Group,
  Modal,
  Pagination,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, podatki, type Shema } from '../api/odjemalec';
import { koliko, stevilo } from '../oblika/oblika';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { PREJEMNIKI, skritaKopija } from './okroznica';
import { cas } from './skrbnistvo';
import { glave, obNapaki } from './zeton';

type Sporocilo = Shema['EpostaSporocilo'];

const VELIKOST = 25;

const VRSTE: Record<string, string> = {
  REGISTRACIJA: 'Registracija',
  PONASTAVITEV_GESLA: 'Pozabljeno geslo',
  OBVESTILO: 'Obvestilo skrbnikom',
  PREIZKUS: 'Preizkus',
  OKROZNICA: 'Okrožnica',
  OKROZNICA_PREIZKUS: 'Okrožnica – preizkus',
  POVPRASEVANJE: 'Povpraševanje',
};

/**
 * Skrbništvo → E-pošta → Poslana sporočila: evidenca vseh sporočil, ki jih je aplikacija poslala ali poskušala
 * poslati (sistemska in iz instanc), z iskanjem, stranmi na strežniku in vsebino (žetoni iz povezav zakriti).
 * {@code vrsta} = samo vrste s tem začetkom (Okrožnice: {@code OKROZNICA}).
 */
export function PoslanaEposta({
  vrsta,
  naslov = 'Poslana sporočila',
  prazno = 'Aplikacija še ni poslala nobenega sporočila.',
}: {
  vrsta?: string;
  naslov?: string;
  prazno?: string;
}) {
  const [iskanje, setIskanje] = useState('');
  const [zakasnjeno] = useDebouncedValue(iskanje.trim(), 300);
  const [stran, setStran] = useState(0);
  const [izbrano, setIzbrano] = useState<number | null>(null);
  const seznam = useQuery({
    queryKey: ['skrbnik', 'eposta-poslana', vrsta, zakasnjeno, stran],
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      try {
        return await podatki(
          api.GET('/api/skrbnik/eposta/poslana', {
            signal,
            headers: glave(),
            params: { query: { iskanje: zakasnjeno || undefined, vrsta, stran, velikost: VELIKOST } },
          }),
        );
      } catch (e) {
        obNapaki(e);
        throw e;
      }
    },
  });
  const skupaj = seznam.data?.skupaj ?? 0;
  const strani = Math.max(1, Math.ceil(skupaj / VELIKOST));

  return (
    <Kartica naslov={naslov}>
      <Stack gap="sm">
        <TextInput
          aria-label="Iskanje po poslanih sporočilih"
          placeholder="Prejemnik, zadeva ali vrsta"
          leftSection={<IconSearch size={16} />}
          value={iskanje}
          onChange={(e) => {
            setIskanje(e.currentTarget.value);
            setStran(0);
          }}
          maw={420}
        />
        {!seznam.data ? (
          <StanjeNalaganja
            napaka={seznam.error}
            ponovi={() => void seznam.refetch()}
            naslov="Poslanih sporočil ni bilo mogoče naložiti"
          />
        ) : seznam.data.vsebina?.length ? (
          <>
            <Table.ScrollContainer minWidth={820}>
              <Table highlightOnHover verticalSpacing={8}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Čas</Table.Th>
                    <Table.Th>Vrsta</Table.Th>
                    <Table.Th>Instanca</Table.Th>
                    <Table.Th>Prejemnik</Table.Th>
                    <Table.Th>Zadeva</Table.Th>
                    <Table.Th>Stanje</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {seznam.data.vsebina.map((s) => (
                    <Table.Tr
                      key={s.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setIzbrano(s.id ?? null)}
                      aria-label={`Sporočilo ${s.zadeva} za ${s.prejemnik}`}
                    >
                      <Table.Td className="brez-preloma">{cas(s.cas)}</Table.Td>
                      <Table.Td>{VRSTE[s.vrsta ?? ''] ?? s.vrsta}</Table.Td>
                      <Table.Td>
                        <Instanca companyId={s.companyId} />
                      </Table.Td>
                      <Table.Td>
                        <Prejemnik s={s} />
                      </Table.Td>
                      <Table.Td>{s.zadeva}</Table.Td>
                      <Table.Td>
                        <Stanje s={s} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {stevilo(stran * VELIKOST + 1)}–{stevilo(Math.min(skupaj, (stran + 1) * VELIKOST))} od {stevilo(skupaj)}
              </Text>
              {strani > 1 && (
                <Pagination total={strani} value={stran + 1} onChange={(v) => setStran(v - 1)} size="sm" />
              )}
            </Group>
          </>
        ) : (
          <Text size="sm" c="dimmed">
            {zakasnjeno ? 'Ni sporočil, ki bi ustrezala iskanju.' : prazno}
          </Text>
        )}
      </Stack>
      <Vsebina id={izbrano} zapri={() => setIzbrano(null)} />
    </Kartica>
  );
}

function Instanca({ companyId }: { companyId?: number | null }) {
  if (companyId === undefined || companyId === null) {
    return (
      <Text size="sm" c="dimmed">
        sistem
      </Text>
    );
  }
  return (
    <Anchor component={Link} to={`/skrbnistvo/${companyId}`} size="sm" onClick={(e) => e.stopPropagation()}>
      #{companyId}
    </Anchor>
  );
}

function Prejemnik({ s }: { s: Sporocilo }) {
  const skriti = skritaKopija(s).length;
  return skriti ? (
    <>
      {koliko(skriti, PREJEMNIKI)}{' '}
      <Text span size="xs" c="dimmed">
        (skrita kopija)
      </Text>
    </>
  ) : (
    s.prejemnik
  );
}

function Stanje({ s }: { s: Sporocilo }) {
  return s.uspelo ? (
    <Badge color="green" variant="light">
      poslano
    </Badge>
  ) : (
    <Tooltip label={s.napaka} multiline maw={360} disabled={!s.napaka}>
      <Badge color="red" variant="light">
        ni poslano
      </Badge>
    </Tooltip>
  );
}

/** Podrobnosti s vsebino: videz HTML (v izoliranem okvirju brez skript) in navadno besedilo. */
function Vsebina({ id, zapri }: { id: number | null; zapri: () => void }) {
  const sporocilo = useQuery({
    queryKey: ['skrbnik', 'eposta-poslana', 'sporocilo', id],
    enabled: id !== null,
    queryFn: async ({ signal }) => {
      try {
        return await podatki(
          api.GET('/api/skrbnik/eposta/poslana/{id}', { signal, headers: glave(), params: { path: { id: id! } } }),
        );
      } catch (e) {
        obNapaki(e);
        throw e;
      }
    },
  });
  const s = sporocilo.data;
  return (
    <Modal opened={id !== null} onClose={zapri} title={s?.zadeva ?? 'Sporočilo'} size="xl" radius="lg">
      {!s ? (
        <StanjeNalaganja
          napaka={sporocilo.error}
          ponovi={() => void sporocilo.refetch()}
          naslov="Sporočila ni bilo mogoče naložiti"
        />
      ) : (
        <Stack gap="sm">
          <Podatki
            vrstice={[
              ['Čas', cas(s.cas)],
              ['Vrsta', VRSTE[s.vrsta ?? ''] ?? s.vrsta],
              ['Instanca', <Instanca key="i" companyId={s.companyId} />],
              ['Prejemnik', s.prejemnik],
              ...(s.skritaKopija
                ? [['Skrita kopija', koliko(skritaKopija(s).length, PREJEMNIKI)] as [string, string]]
                : []),
              ...(s.odgovorNa ? [['Odgovori na', s.odgovorNa] as [string, string]] : []),
              ...(s.poslal ? [['Poslal', s.poslal] as [string, string]] : []),
              ['Stanje', <Stanje key="s" s={s} />],
            ]}
          />
          {s.skritaKopija && (
            <Text size="sm" style={{ overflowWrap: 'anywhere' }} aria-label="Prejemniki v skriti kopiji">
              {skritaKopija(s).join(', ')}
            </Text>
          )}
          {!s.uspelo && s.napaka && (
            <Alert color="red" variant="light">
              {s.napaka}
            </Alert>
          )}
          <Text size="xs" c="dimmed">
            Žetoni iz povezav (registracija, ponastavitev gesla) so v evidenci zakriti.
          </Text>
          <Tabs defaultValue={s.html ? 'videz' : 'besedilo'} keepMounted={false}>
            <Tabs.List>
              {s.html && <Tabs.Tab value="videz">Videz</Tabs.Tab>}
              <Tabs.Tab value="besedilo">Besedilo</Tabs.Tab>
            </Tabs.List>
            {s.html && (
              <Tabs.Panel value="videz" pt="sm">
                <iframe
                  title="Videz sporočila"
                  sandbox=""
                  srcDoc={s.html}
                  style={{ width: '100%', height: 520, border: 0, borderRadius: 8 }}
                />
              </Tabs.Panel>
            )}
            <Tabs.Panel value="besedilo" pt="sm">
              <Text size="sm" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {s.besedilo}
              </Text>
            </Tabs.Panel>
          </Tabs>
        </Stack>
      )}
    </Modal>
  );
}
